import React, { Component } from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import auth from '@react-native-firebase/auth';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import { UserServiceClient } from 'fitbeat.api-clients';
import { Button, TextInput, FormError, FormKeyboardSpacer } from 'fitbeat.react-native-components';
import { Text, View, StyleSheet, Keyboard, ScrollView, Platform, TouchableOpacity } from 'react-native';
import Storage from '../utils/Storage';
import { getApiConfig } from '../config';
import { SocialLogin, SocialAuthLoader, TermsAndConditions } from './components';
import { isRequired, isEmailValid } from '../utils/validator';
import { isDesktop, isWeb, parseEmailForTextFieldFromURL } from '../utils';
import firebaseErrorHandler from '../utils/FirebaseErrors';
import { GoogleAnalytics } from '../services/google-analytics';
import { clearSignupError } from './actions';
import { isMobileWeb } from './SignupFormsStyles';
import Theme from '../theme';
import { focusOnInput } from '../utils/FocusOnInput';
import { defaultScale, scaleText } from '../utils/scaling';
import { Logger } from '../utils/Logger';
import StudioUserExistsError from './components/StudioUserExistsError'
import { addDeepLinkParams } from '../utils/notifications/actions';

class Login extends Component {
  googleAnalytics = new GoogleAnalytics();
  unAuthenticatedUserServiceClient = new UserServiceClient(getApiConfig(), fetch, Logger.debug, undefined);
  state = {
    isLoading: false,
    isSocialAuthLoading: false,
    studioAccountAlreadyExists: false
  };

  async componentDidMount() {
    const { initialize, deepLinkedEmail } = this.props;
    if (deepLinkedEmail) {
      const parsedEmail = parseEmailForTextFieldFromURL(deepLinkedEmail);
      initialize({ email: parsedEmail })
    }

    this.googleAnalytics.setCurrentScreen('Login', 'Login');
    await Storage.remove('signoutRoute');
    await Storage.remove('basicUserInfo');
    await Storage.remove('guardianInfo');
  }

  dismissKeyboard = () => {
    Keyboard.dismiss();
  }

  onSubmit = async (values) => {
    this.dismissKeyboard();
    const { email, password } = values;
    const { navigation, addToDeepLinkParams} = this.props;
    this.setState({ isLoading: true, visible: true });
    try {
      const preSignupAccountWithEmail = await this.unAuthenticatedUserServiceClient.getUserForPreAccountByEmail(
        values.email);
      const isExisitingUserWithPreToken = preSignupAccountWithEmail && preSignupAccountWithEmail.continueSignupToken;
      if (isExisitingUserWithPreToken) {
        this.setState({
          studioAccountAlreadyExists: true,
          isLoading: false,
          continueSignupToken: preSignupAccountWithEmail.continueSignupToken,
        });
        return;
      }
      this.props.clearSignupError();
      const userCredential = await auth().signInWithEmailAndPassword(email, password);
      navigation.navigate('AuthLoading');
      this.onLoginSuccess(userCredential.user.uid);
    } catch (error) {
      throw new SubmissionError({ _error: firebaseErrorHandler(error) });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  socialAuthSuccess = (user) => {
    this.onLoginSuccess(user.user.uid);
    this.setIsSocialAuthLoading(false);
  }

  onLoginSuccess= (uid) => {
    Logger.debug('[LoginScreen] login success with id: ' + uid);
    this.googleAnalytics.setUserId(uid);
    this.googleAnalytics.logEventWithParamaters('login');
  }

  setIsSocialAuthLoading = (isSocialAuthLoading) => {
    this.setState({ isSocialAuthLoading });
    if (isSocialAuthLoading) {
      this.props.clearSignupError();
    }
  }

  forgotPassword = () => {
    this.props.navigation.navigate('ForgotPassword');
  }

  headerTitle = () => {
    const {
      t,
      existingUserOpenedAfterDeeplink,
      existingUserData,
    } = this.props;
    if (existingUserOpenedAfterDeeplink) {
      return t('alreadySignedUpPleaseBook');
    }
    if (existingUserData && existingUserData.firstname) {
      return t('loginScreenHeaderWithName', {name: existingUserData.firstname});
    }
    return t('loginScreenHeaderDefault');
  }

  render() {
    const {
      t,
      error,
      handleSubmit,
      signupError,
    } = this.props;
    const { isLoading, isSocialAuthLoading, studioAccountAlreadyExists, continueSignupToken } = this.state;

    if (isSocialAuthLoading) {
      return (
        <SocialAuthLoader />
      );
    }
    return (
      <View style={styles.container}>
        <FormKeyboardSpacer>
          {!isWeb() &&
            <Text style={styles.infoHeader}>{this.headerTitle()}</Text>
          }
          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.contentContainerStyle}>
            <View>
              <Field
                trim
                forwardRef
                name="email"
                placeholder={t('formFields:email')}
                returnKeyType="next"
                autoCapitalize="none"
                component={TextInput}
                validate={isEmailValid}
                keyboardType="email-address"
                blurOnSubmit={false}
                ref={(ref) => { this.emailInput = ref; }}
                onSubmitEditing={() => focusOnInput(this.passwordInput)}
              />
              <Field
                forwardRef
                name="password"
                secureTextEntry
                component={TextInput}
                autoCapitalize="none"
                placeholder={t('formFields:password')}
                returnKeyType="done"
                validate={isRequired}
                ref={(ref) => { this.passwordInput = ref; }}
                onSubmitEditing={handleSubmit(this.onSubmit)}
              />

              <FormError error={error || signupError} color={Theme.GREY_DARK} />
              { studioAccountAlreadyExists && continueSignupToken &&
                <StudioUserExistsError t={t} navigation={this.props.navigation}
                                       continueSignupToken={continueSignupToken}/>
              }
              <Button
                title={t('login')}
                isLoading={isLoading}
                fontColor={Theme.WHITE}
                onPress={handleSubmit(this.onSubmit)}
                overrideFontStyles={{ fontFamily: Theme.Custom800 }}
                containerStyle={styles.submitButtonContainer}
              />

              <Text
                onPress={this.forgotPassword}
                style={[styles.forgotPasswordText, { color: Theme.PRIMARY }]}>
                {t('forgotPassword?')}
              </Text>

              <View style={styles.orContainer}>
                <View style={styles.line} />
              </View>
            </View>

            <View style={styles.tandcContainer}>
              <TermsAndConditions
                preAgreementText={t('byContinuingYouAgreeToTandC')}
                canAccept={false}
                showMinor={false}
                textStyle={styles.tandcText}
              />
            </View>
          </ScrollView>
        </FormKeyboardSpacer>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: defaultScale(15),
    paddingTop: isMobileWeb ? defaultScale(48) : 0,
    backgroundColor: Theme.WHITE,
  },
  contentContainerStyle: {
    flexGrow: 1,
    justifyContent: 'space-between',
    flexDirection: 'column',
    paddingTop: defaultScale(15),
  },
  socialContainer: {
    paddingVertical: defaultScale(10),
    flexDirection: 'row',
  },
  submitButtonContainer: {
    marginVertical: defaultScale(5),
  },
  orContainer: {
    paddingBottom: defaultScale(10),
    alignItems: 'center',
    flexDirection: 'row',
  },
  line: {
    flex: 1,
    height: 0.5,
  },
  orText: {
    fontFamily: Theme.CustomBold,
    marginHorizontal: defaultScale(10),
    color: Theme.CHARCOAL,
  },
  footer: {
    justifyContent: 'center',
    flexDirection: 'row',
  },
  footerText: {
    fontSize: 14,
    fontFamily: Theme.CustomBold,
    textAlign: 'center',
    color: Theme.GREY_MEDIUM_LIGHT,
  },
  textHighlight: {
    fontSize: 14,
    fontFamily: Theme.CustomBold,
    color: Theme.PRIMARY,
  },
  forgotPasswordText: {
    ...Theme.textStyle.bodyCopyMed,
    textAlign: 'right',
    paddingVertical: defaultScale(5),
    paddingBottom: defaultScale(10),
  },
  tandcText: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_MEDIUM,
  },
  tandcContainer: {
    paddingBottom: defaultScale(20),
  },
  infoHeader: {
    ...Theme.textStyle.heading2,
    marginTop: defaultScale( isDesktop() ? 30 : 0),
  }
});

Login = translate(['auth', 'formFields'], { wait: true })(Login)
const mapStateToProps = (state) => {
  return {
    signupError: state.auth.initialUserSetup.signupError ? {
      message: state.auth.initialUserSetup.signupError,
    } : null,
    deepLinkedEmail: state.deepLinkParamaters.email,
    existingUserOpenedAfterDeeplink: state.deepLinkParamaters.existingUserLogin,
    existingUserData: state.auth.signup.userExistsCheckData,
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    clearSignupError: () => dispatch(clearSignupError()),
    addToDeepLinkParams : (params) => dispatch(addDeepLinkParams(params))
  }
}
export default connect(mapStateToProps, mapDispatchToProps)(reduxForm({ form: 'LoginForm' })(Login));
